// BzKeyProvider — the client half of SPEC §5.2 (Model A; Model B reuses this
// derivation in M4 with an out-of-band secret).
//
// The raw session secret is NEVER used as the media key. We derive
//   keyMaterial = HKDF-SHA256(secret, salt = sessionId, info = "bz-e2ee-v1")
// so reusing a meeting link across sessions yields cryptographically isolated
// keys (sessionId is unique per room instance). Because sessionId and secret
// are identical for every admitted attendee, all peers derive the same
// material and interoperate.
//
// Format note: LiveKit's E2EE worker expects HKDF *key material* (it runs its
// own final AES-GCM derivation with a fixed ratchet salt). So our §5.2 HKDF is
// the OUTER layer providing session isolation; the worker's derivation is an
// inner, deterministic, peer-identical step. We hand the worker our derived
// bytes re-imported as HKDF material — exactly the shape the SDK's own
// ExternalE2EEKeyProvider produces.
//
// §5.3: material is non-extractable once imported; frame crypto runs in the
// worker; nothing is written to storage. Zeroization on disconnect = dropping
// the provider (its material becomes unreachable) + the SDK terminating the
// worker. Any change to these semantics requires human approval (§16.1).

import { BaseKeyProvider } from 'livekit-client';

import { deriveDataKey } from '../sealed';

export const HKDF_INFO = 'bz-e2ee-v1';
export const FILE_HKDF_INFO = 'bz-file-v1';

export class BzKeyProvider extends BaseKeyProvider {
  // Kept for per-file key derivation (F-COM-5): the session secret re-imported
  // as non-extractable HKDF material. Never leaves the client.
  private fileIkm: CryptoKey | null = null;

  // Session data-plane key (sealed.ts): AES-GCM over every data-channel
  // payload. LiveKit's worker encrypts media frames only, so chat, polls,
  // reactions and file refs are sealed app-side under this key.
  dataKey: CryptoKey | null = null;

  constructor() {
    // Shared room key (v1 model, §5.5: no per-participant keys yet).
    // ratchetWindowSize 0 + failureTolerance -1 mirror the SDK's shared-key
    // posture: a wrong key never auto-ratchets or gets marked invalid.
    super({ sharedKey: true, ratchetWindowSize: 0, failureTolerance: -1 });
  }

  /** Derive a per-file AES-GCM key: HKDF(sessionSecret, salt=fileId,
   * info="bz-file-v1") — F-COM-5. Requires setSessionSecret to have run. */
  async deriveFileKey(fileId: string): Promise<CryptoKey> {
    if (!this.fileIkm) throw new Error('no session key for file derivation');
    const enc = new TextEncoder();
    return crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt: enc.encode(fileId), info: enc.encode(FILE_HKDF_INFO) },
      this.fileIkm,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Derive and install the media key for this session.
   * @param secret Model A: base64 server secret. Model B: the user's
   *   passphrase / URL-fragment value (interpreted as UTF-8).
   * @param kind which interpretation to use.
   */
  async setSessionSecret(
    secret: string,
    sessionId: string,
    kind: 'base64' | 'passphrase' = 'base64',
  ): Promise<void> {
    const ikmBytes = kind === 'base64' ? base64ToBytes(secret) : utf8ToBytes(secret);
    const ikm = await crypto.subtle.importKey('raw', ikmBytes, 'HKDF', false, ['deriveBits']);
    ikmBytes.fill(0); // drop our only JS-side copy of the raw secret

    const enc = new TextEncoder();
    // §5.2 outer HKDF: salt = sessionId, info = "bz-e2ee-v1".
    const derivedBits = await crypto.subtle.deriveBits(
      { name: 'HKDF', hash: 'SHA-256', salt: enc.encode(sessionId), info: enc.encode(HKDF_INFO) },
      ikm,
      256,
    );
    const derived = new Uint8Array(derivedBits);
    // Re-import as HKDF material (non-extractable): the worker performs its
    // own final AES-GCM derivation from this, identically for every peer.
    const material = await crypto.subtle.importKey('raw', derived, 'HKDF', false, [
      'deriveBits',
      'deriveKey',
    ]);
    // Keep a separate HKDF-material copy for per-file key derivation (F-COM-5).
    this.fileIkm = await crypto.subtle.importKey('raw', derived, 'HKDF', false, ['deriveKey']);
    // Data-plane key must be cut before the derived bytes are zeroed.
    this.dataKey = await deriveDataKey(derived, sessionId);
    derived.fill(0);
    this.onSetEncryptionKey(material);
  }
}

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function utf8ToBytes(s: string): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder().encode(s);
  const out = new Uint8Array(new ArrayBuffer(enc.length));
  out.set(enc);
  return out;
}
